// src/pages/MusterPage.tsx
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchMuster } from "../../store/slices/musterSlice";
import { RootDispatch, RootState } from "../../store";
import { exportToExcel } from "../../utils/excel.util";
import { lockOrUnlockUser } from "../../store/slices/musterSlice";

/* --- Types --- */
type SessionStatus =
  | "Present"
  | "Absent"
  | "Paid Leave"
  | "Sick Leave"
  | "Casual Leave"
  | "CompOff"
  | "Loss of Pay"
  | "Weekend"
  | "Holiday";

type DayStatus =
  | SessionStatus
  | { firstSession: SessionStatus; secondSession: SessionStatus };

interface EmployeeMusterRow {
  userId: string;
  employeeNo?: string;
  name?: string;
  records?: { day: number; status: DayStatus }[];
  attendance?: { day: number; status: DayStatus }[];
  isLockedFromApplying?: boolean;
}

/* --- Muted professional styles (Tailwind classes) --- */
const STATUS_STYLES: Record<
  Exclude<SessionStatus, "Weekend">,
  { bg: string; text: string; ring?: string }
> = {
  Present: {
    bg: "bg-green-100",
    text: "text-green-800",
    ring: "ring-green-200",
  },
  Absent: { bg: "bg-rose-100", text: "text-rose-800", ring: "ring-rose-200" },
  "Paid Leave": {
    bg: "bg-sky-100",
    text: "text-sky-800",
    ring: "ring-sky-200",
  },
  "Sick Leave": {
    bg: "bg-amber-100",
    text: "text-amber-800",
    ring: "ring-amber-200",
  },
  "Casual Leave": {
    bg: "bg-violet-100",
    text: "text-violet-800",
    ring: "ring-violet-200",
  },
  CompOff: {
    bg: "bg-stone-100",
    text: "text-stone-800",
    ring: "ring-stone-200",
  },
  "Loss of Pay": {
    bg: "bg-fuchsia-100",
    text: "text-fuchsia-800",
    ring: "ring-fuchsia-200",
  },
  Holiday: {
    bg: "bg-slate-100",
    text: "text-slate-800",
    ring: "ring-slate-200",
  },
};

/* weekend style separately (neutral) */
const WEEKEND_STYLE = {
  bg: "bg-slate-50",
  text: "text-slate-600",
  ring: "ring-slate-100",
};

/* --- Helpers --- */
const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate(); // month is 1-12
}
function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

export function getAbbrev(status: string) {
  switch (status) {
    case "Present":
      return "P";
    case "Absent":
      return "A";
    case "Paid Leave":
      return "PL";
    case "Sick Leave":
      return "SL";
    case "Casual Leave":
      return "CL";
    case "CompOff":
      return "CO";
    case "Holiday":
      return "H";
    case "Loss of Pay":
      return "LOP";
    case "Weekend":
      return "W";
    default:
      return status.slice(0, 2).toUpperCase();
  }
}

function styleFor(status: SessionStatus) {
  if (status === "Weekend") return WEEKEND_STYLE;
  const s = STATUS_STYLES[status as Exclude<SessionStatus, "Weekend">];
  return (
    s ?? { bg: "bg-gray-100", text: "text-gray-800", ring: "ring-gray-200" }
  );
}

/* statuses list for summary columns (order matters) */
export const STATUS_ORDER: SessionStatus[] = [
  "Present",
  "Absent",
  "Paid Leave",
  "Sick Leave",
  "Casual Leave",
  "CompOff",
  "Holiday",
  "Loss of Pay",
  "Weekend",
];

/* --- Cell renderer (shows abbreviation(s) only) --- */
function DayCell({ status }: { status: DayStatus }) {
  if (typeof status === "string") {
    const s = styleFor(status as SessionStatus);
    const abbrev = getAbbrev(status);
    return (
      <div
        title={status}
        className={`h-8 w-8 ${s.bg} ${
          s.text
        } rounded-md grid place-items-center text-[12px] font-semibold ring-1 ${
          s.ring ?? "ring-gray-200"
        }`}
      >
        <span>{abbrev}</span>
      </div>
    );
  }

  // two different statuses — show side-by-side halves
  const firstStyle = styleFor(status.firstSession);
  const secondStyle = styleFor(status.secondSession);
  const a1 = getAbbrev(status.firstSession);
  const a2 = getAbbrev(status.secondSession);

  return (
    <div className="h-8 w-8 flex rounded-md overflow-hidden ring-1 ring-slate-100">
      <div
        className={`flex-1 flex items-center justify-center text-[10px] font-semibold ${
          firstStyle.bg
        } ${firstStyle.text} ${firstStyle.ring ?? ""}`}
      >
        {a1}
      </div>
      <div
        className={`flex-1 flex items-center justify-center text-[10px] font-semibold ${
          secondStyle.bg
        } ${secondStyle.text} ${secondStyle.ring ?? ""}`}
      >
        {a2}
      </div>
    </div>
  );
}

/* compute per-row summary counts (dual sessions count 0.5 each) */
function computeRowSummary(row: EmployeeMusterRow) {
  const counts: Record<string, number> = {};
  for (const k of STATUS_ORDER) counts[k] = 0;
  const recs = row.records ?? row.attendance ?? [];
  for (const r of recs) {
    const st = r.status;
    if (typeof st === "string") {
      counts[st] = (counts[st] || 0) + 1;
    } else {
      counts[st.firstSession] = (counts[st.firstSession] || 0) + 0.5;
      counts[st.secondSession] = (counts[st.secondSession] || 0) + 0.5;
    }
  }
  return counts;
}

/* --- Page --- */
export default function MusterPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [year, setYear] = useState(now.getFullYear());
  const [search, setSearch] = useState("");

  const dispatch = useDispatch<RootDispatch>();
  // your slice keeps { muster, loading, error } per earlier slice code
  const { muster, loading, error } = useSelector(
    (state: RootState) => state.muster
  );

  const totalDays = useMemo(() => getDaysInMonth(year, month), [year, month]);

  useEffect(() => {
    dispatch(fetchMuster({ month, year }));
  }, [dispatch, month, year]);

  // normalize rows => use `records`
  const rows: EmployeeMusterRow[] = (muster ?? []).map((r: any) => ({
    ...r,
    records: r.records ?? r.attendance ?? [],
  }));

  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [lockedUsers, setLockedUsers] = useState<string[]>([]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    let res = rows.filter((r) =>
      [r.name, r.employeeNo]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );

    if (selectedUsers.length > 0) {
      res = res.filter((r) => selectedUsers.includes(r.userId));
    }

    return res;
  }, [rows, search]);

  const toggleLock = (userId: string) => {
    const isLocked =
      muster.find((r) => r.userId === userId)?.isLockedFromApplying ?? false;
    dispatch(lockOrUnlockUser({ userId, lock: !isLocked }));
  };

  const toggleLockSelected = () => {
    selectedUsers.forEach((userId) => {
      const isLocked =
        muster.find((r) => r.userId === userId)?.isLockedFromApplying ?? false;
      dispatch(lockOrUnlockUser({ userId, lock: !isLocked }));
    });
  };

  const allUsers = useMemo(
    () => rows.map((r) => ({ id: r.userId, name: r.name ?? "Unnamed" })),
    [rows]
  );

  function goPrevMonth() {
    const d = new Date(year, month - 2, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }
  function goNextMonth() {
    const d = new Date(year, month, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }

  const toggleSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleExport = () => {
    const dataToExport =
      selectedUsers.length > 0
        ? rows.filter((r) => selectedUsers.includes(r.userId))
        : rows;

    exportToExcel(dataToExport, "Muster_Report", "Muster");
  };

  return (
    <div className="w-full h-full flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrevMonth}
            className="px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
          >
            ◀
          </button>
          <div className="text-lg font-semibold text-slate-800 min-w-[180px] text-center">
            {monthNames[month - 1]} {year}
          </div>
          <button
            onClick={goNextMonth}
            className="px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
          >
            ▶
          </button>
          <div className="flex gap-2">
            <button
              onClick={toggleLockSelected}
              className="rounded-lg px-3 py-2.5 text-sm transition-all block w-full bg-navyBlue-primary text-white font-medium
             hover:bg-blue-500 active:scale-95 focus:ring-2 focus:ring-blue-300"
              disabled={selectedUsers.length === 0}
            >
              Lock Selected
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or Employee ID"
            className="px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200 min-w-[260px] outline-none focus:ring-slate-400"
          />
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            className="px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200"
          >
            {monthNames.map((mName, idx) => (
              <option key={mName} value={idx + 1}>
                {mName}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) =>
              setYear(parseInt(e.target.value || String(now.getFullYear())))
            }
            className="w-28 px-3 py-2 rounded-xl bg-white shadow-sm ring-1 ring-slate-200"
          />
          <button
            onClick={handleExport}
            className="rounded-lg px-3 py-2.5 text-sm transition-all block w-full bg-navyBlue-primary text-white font-medium
             hover:bg-blue-500 active:scale-95 focus:ring-2 focus:ring-blue-300"
          >
            Export to Excel
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200 p-3">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {[
            "Present",
            "Absent",
            "Paid Leave",
            "Sick Leave",
            "Casual Leave",
            "CompOff",
            "Holiday",
            "Loss of Pay",
            "Weekend",
          ].map((k) => {
            const s = (k === "Holiday"
              ? STATUS_STYLES["Holiday"]
              : (STATUS_STYLES as any)[k]) ?? {
              bg: "bg-gray-100",
              text: "text-gray-800",
            };
            const bg = k === "Weekend" ? WEEKEND_STYLE.bg : s.bg;
            const text = k === "Weekend" ? WEEKEND_STYLE.text : s.text;
            return (
              <div key={k} className="flex items-center gap-1 mr-2">
                <span
                  className={`inline-block h-3 w-6 rounded ${bg} ${text} ring-1`}
                />
                <span className="text-slate-600">{k}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Calendar table with per-row summary columns at the end */}
      <div className="w-full overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50">
            <tr>
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={
                    selectedUsers.length === rows.length && rows.length > 0
                  }
                  onChange={(e) =>
                    setSelectedUsers(
                      e.target.checked ? rows.map((r) => r.userId) : []
                    )
                  }
                />
              </th>
              <th className="sticky left-0 z-20 bg-slate-50 text-left px-3 py-2 font-semibold text-slate-700 border-r border-slate-200 min-w-[200px]">
                Employee
              </th>
              {/* day headers */}
              {Array.from({ length: totalDays }, (_, i) => i + 1).map((d) => (
                <th
                  key={d}
                  className="px-0 py-2 font-semibold text-slate-600 text-center w-8"
                >
                  {d}
                </th>
              ))}
              {/* per-user summary headers */}
              {STATUS_ORDER.map((st) => (
                <th
                  key={st}
                  className="px-2 py-2 font-semibold text-slate-600 text-center min-w-[48px]"
                  title={st}
                >
                  {getAbbrev(st)}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={totalDays + 2 + STATUS_ORDER.length}
                  className="px-4 py-6 text-center text-slate-500"
                >
                  Loading muster…
                </td>
              </tr>
            )}

            {error && !loading && (
              <tr>
                <td
                  colSpan={totalDays + 2 + STATUS_ORDER.length}
                  className="px-4 py-6 text-center text-red-600"
                >
                  {error}
                </td>
              </tr>
            )}

            {!loading && !error && filteredRows.length === 0 && (
              <tr>
                <td
                  colSpan={totalDays + 2 + STATUS_ORDER.length}
                  className="px-4 py-6 text-center text-slate-500"
                >
                  No employees to display.
                </td>
              </tr>
            )}

            {filteredRows.map((row) => {
              const summary = computeRowSummary(row);
              return (
                <tr key={row.userId} className="odd:bg-white even:bg-slate-50">
                  {/* Checkbox column */}
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(row.userId)}
                      onChange={() => toggleSelection(row.userId)}
                    />
                  </td>
                  <td className="sticky left-0 z-10 bg-inherit px-3 py-2 border-r border-slate-200 min-w-[200px]">
                    <div className="flex items-center justify-between">
                      {/* Left side: Avatar + Name */}
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-200 grid place-items-center text-[10px] text-slate-600">
                          {row.name?.[0] ?? "?"}
                        </div>
                        <div className="flex flex-col leading-tight">
                          <span className="text-[13px] font-semibold text-slate-800">
                            {row.name ?? "Unnamed"}
                          </span>
                          <span className="text-[11px] text-slate-500">
                            {row.employeeNo ?? row.userId}
                          </span>
                        </div>
                      </div>

                      {/* Right side: Lock/Unlock button */}
                      <button
                        onClick={() => toggleLock(row.userId)}
                        className="h-8 w-8 flex items-center justify-center text-lg rounded-md bg-slate-100 hover:bg-slate-200 ring-1 ring-slate-200"
                        title={row.isLockedFromApplying ? "Locked" : "Unlocked"}
                      >
                        {row.isLockedFromApplying ? "🔒" : "🔓"}
                      </button>
                    </div>
                  </td>

                  {/* day cells */}
                  {Array.from({ length: totalDays }, (_, i) => i + 1).map(
                    (d) => {
                      const source = row.records ?? row.attendance ?? [];
                      const rec = source.find(
                        (r: { day: number }) => r.day === d
                      );
                      const status: DayStatus = rec?.status ?? "Absent";
                      return (
                        <td
                          key={d}
                          className="border border-gray-200 w-8 min-w-[32px] text-center"
                        >
                          <div className="flex justify-center">
                            <DayCell status={status} />
                          </div>
                        </td>
                      );
                    }
                  )}

                  {/* per-user summary cells */}
                  {STATUS_ORDER.map((st) => {
                    const val = summary[st] ?? 0;
                    const style =
                      st === "Weekend"
                        ? WEEKEND_STYLE
                        : (STATUS_STYLES as any)[st] ?? {
                            bg: "bg-gray-100",
                            text: "text-gray-800",
                          };
                    return (
                      <td key={st} className="px-2 py-1 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span
                            className={`inline-block h-6 w-8 rounded ${style.bg} ${style.text} ring-1 text-xs font-medium grid place-items-center`}
                          >
                            {Number(val % 1 === 0 ? val : val.toFixed(1))}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
